/**
 * ProBetaRequestForm — shared "Request Beta PRO access" form.
 * Web payments aren't live yet: requests are reviewed manually
 * (Admin → Ext. API → PRO Beta Requests). Used on /mcp, /pro.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, Check, Flame } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../AuthContext";

export const ProBetaRequestForm = ({ dark = false }) => {
  const { user, authHeader } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);
  const [spots, setSpots] = useState(null);

  useEffect(() => {
    axios.get(`${API}/mcp/beta-spots`).then(r => setSpots(r.data)).catch(() => {});
  }, []);

  const inputStyle = dark
    ? { background: "#1a2122", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "12px 16px", fontSize: 14, color: "#e2e8f0", outline: "none", width: "100%" }
    : { background: "#fff", border: "1px solid #dfe5e7", borderRadius: 12, padding: "12px 16px", fontSize: 14, color: "#191c1d", outline: "none", width: "100%" };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/mcp/request-pro-beta`,
        {
          name: name.trim() || undefined,
          email: user ? undefined : email.trim(),
          company: company.trim() || undefined,
          message: message.trim() || undefined,
        },
        { headers: authHeader() }
      );
      setDone(data.message);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(err?.response?.status === 429
        ? "Too many requests — try again in an hour."
        : typeof detail === "string" ? detail : "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="pro-beta-success"
        style={{ display: "flex", gap: 10, alignItems: "flex-start", background: dark ? "rgba(52,211,153,0.08)" : "#ecfdf5", border: `1px solid ${dark ? "rgba(52,211,153,0.3)" : "#a7f3d0"}`, borderRadius: 14, padding: 18 }}
      >
        <Check style={{ width: 16, height: 16, color: "#10b981", flexShrink: 0, marginTop: 2 }} />
        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: dark ? "#34d399" : "#047857" }}>{done}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="pro-beta-form" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {spots && (
        <div data-testid="pro-beta-spots" style={{ display: "inline-flex", alignItems: "center", gap: 7, alignSelf: "flex-start", background: dark ? "rgba(124,58,237,0.12)" : "#f3ecfe", border: `1px solid ${dark ? "rgba(167,139,250,0.3)" : "#ddd0f9"}`, borderRadius: 999, padding: "6px 14px" }}>
          <Flame style={{ width: 13, height: 13, color: "#7c3aed" }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: dark ? "#c4b5fd" : "#6d28d9" }}>
            {spots.remaining > 0
              ? `${spots.remaining} of ${spots.total} Beta spots left`
              : "All Beta spots taken — new requests join the waitlist"}
          </span>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={80}
          placeholder="Your name" data-testid="pro-beta-name-input" style={inputStyle} />
        {user ? (
          <div data-testid="pro-beta-signed-in" style={{ ...inputStyle, display: "flex", alignItems: "center", opacity: 0.8, cursor: "default" }}>
            {user.email}
          </div>
        ) : (
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            placeholder="you@company.com" data-testid="pro-beta-email-input" style={inputStyle} />
        )}
      </div>
      <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} maxLength={120}
        placeholder="Company / organisation (optional)" data-testid="pro-beta-company-input" style={inputStyle} />
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} rows={3}
        placeholder="How do you plan to use ask_tourismintel? (optional)" data-testid="pro-beta-message-input"
        style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
      {error && <p data-testid="pro-beta-error" style={{ margin: 0, fontSize: 13, color: "#ef4444" }}>{error}</p>}
      <button type="submit" disabled={busy} data-testid="pro-beta-submit-btn"
        style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 12, padding: "13px 20px", fontSize: 14, fontWeight: 700, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        <Sparkles style={{ width: 15, height: 15 }} />
        {busy ? "Sending…" : "Request Beta PRO access"}
      </button>
      <p style={{ margin: 0, fontSize: 11, color: dark ? "#64748b" : "#8b9698", lineHeight: 1.6 }}>
        Web payments are coming soon. Beta requests are reviewed manually — we&apos;ll get back to you by email.
      </p>
    </form>
  );
};
